import React, { useEffect, useState, useMemo } from 'react';
import './App.css';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { MantineProvider, ColorSchemeProvider, useMantineTheme } from '@mantine/core';
import { ToastContainer } from 'react-toastify';

import Dashboard from './pages/Dashboard';
import DiskErasure from './pages/DiskErasure';
import ActivityLog from './pages/ActivityLog';
import Layout from './components/common/Layout';
import DriveDetails from './pages/DriveDetails';
import FileExplorer from './pages/FileExplorer';
import Encryption from './pages/Encryption';
import Users from './pages/Users';
import Companies from './pages/Companies';
import Departments from './pages/Departments';
import License from './pages/License';
import Login from './pages/Login';

import AuthContext from './AuthContext';
import ProtectedWrapper from './ProtectedWrapper';

import { getTheme } from './Theme';
import { lavenderTheme } from './MantineTheme';

function App() {
  // === Auth & User State ===
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [user, setUser] = useState('');
  const [userType, setUserType] = useState('');
  const [admin, setAdmin] = useState('');
  const [token, setToken] = useState('');

  // === UI State ===
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
// Initialize mode from localStorage or default to 'light'
const [mode, setMode] = useState(() => localStorage.getItem('mode') || 'light');

// Whenever mode changes, save it to localStorage
useEffect(() => {
  localStorage.setItem('mode', mode);
}, [mode]);

const toggleColorScheme = () => {
  setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
};

  // === MUI Theme ===
  const theme = useMemo(() => getTheme(mode), [mode]);

  // === Auth Functions ===
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUsername(storedUser.name);
      setIsLoggedIn(true);
      setUser(storedUser.id);
      setUserType(storedUser.user_type);
      setAdmin(storedUser.company);
      setToken(storedUser.token);
    }
  }, []);

  const signin = (newUser, callback) => {
    if (newUser && newUser.name) {
      localStorage.setItem('user', JSON.stringify(newUser));
      setUsername(newUser.username);
      setIsLoggedIn(true);
      setUser(newUser.id);
      setUserType(newUser.user_type);
      setAdmin(newUser.company);
      setToken(newUser.token);
      if (callback) callback();
    }
  };

  const signout = () => {
    localStorage.removeItem('user');
    setUsername('');
    setUser('');
    setIsLoggedIn(false);
    setUserType('');
    setAdmin('');
    setToken('');
  };

  const authValue = {
    username,
    user,
    userType,
    admin,
    isLoggedIn,
    token,
    signin,
    signout,
    mode,
    setMode,
    sidebarCollapsed,
    setSidebarCollapsed,
  };

  // === React Router ===
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedWrapper>
              <Layout />
            </ProtectedWrapper>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="/devices" element={<DiskErasure />} />
          <Route path="/activity-log" element={<ActivityLog />} />
          <Route path="/drive-details/:deviceName" element={<DriveDetails />} />
          <Route path="/file-explorer" element={<FileExplorer />} />
          <Route path="/disk-encryption" element={<Encryption />} />
          <Route path="/users" element={<Users />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/departments" element={<Departments />} />
          <Route path="/license" element={<License />} />
          <Route path="*" element={<h1>404 Not Found</h1>} />
        </Route>
      </Route>
    )
  );

  return (
      <MantineProvider
        theme={lavenderTheme(mode)}
        withGlobalStyles
        withNormalizeCSS
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthContext.Provider value={authValue}>
            <RouterProvider router={router} />
            <ToastContainer position="top-right" autoClose={3000} />
          </AuthContext.Provider>
        </ThemeProvider>
      </MantineProvider>
  );
}

export default App;
